import axios from 'axios'

import { computed } from 'vue'
import { defineStore } from 'pinia'
import { useRoute, useRouter } from 'vue-router'
import { useChannelsStore } from '@/stores/channels'
import { useUsersStore } from '@/stores/users'
import { useChatAttendance } from '@/stores/chat/attendances'
import { useChatMessages } from './chat/messages'
import { startWebSocket } from '@/composables/chat/useWebSocket'
import { sendWebSocketMessage } from '@/composables/chat/useWebSocket'
import { notify } from '@kyvg/vue3-notification'
import { getUrlBaseV2, getUrlApiV1 } from '@/utils/index'

let closedRefetchTimer = null

export function useChatStore() {
    const storeAttendance = useChatAttendance()
    const storeUser = useUsersStore
    const storeMessages = useChatMessages()

    return defineStore('chat', {
        state: () => ({
            layout: {
                showAttendanceInMobile: false,
                showPreferencesSlideOver: false,
                showNotificationsSlideOver: false,
                showSidebar: false,
                showSelectAttendances: false,
            },
            loading: {
                requestAttendances: false,
                requestMessages: false,
                attendancesList: false,
                waitingList: false,
            },
            attendances: {
                progress: [],
                waiting: [],
                automation: [],
                closed: [],

            },
            lastAttendance: {
                active: null, // TODO: Standardize in backend
                waiting: null,
                automation: null,
            },
            channels: [],
            users: [],
            online_users: [],
            departaments: [],
            tags: [],
            tagsClient: [],
            tagsKanban: [],
            tagsUser: [],
            messagesFast: [],
            messages: {},
            filterAttendances: '',
            attendanceActive: null,
            selectedAttendances: [],
            filteredTerm: null,
            filteredUsers:[],
            filteredDepartaments:[],
            filteredChannels: [],
            filteredTags: [],
            filterLastInteraction: 'all',
            internalMessages: false,
            lastUpdated: new Date(),
            selectedConnections: {},
            websocket: { status: 'disconnected', token: '' },
            toUsers: {},
            soundAlert: true,
            newMessagesCount: 0,
            focused: true,
            interval: null,
            chatConfig: null,
            templates: '',
            surveyOptions: [],
            automationOptions: [],
            showKeyPad: false,
            incomingcall: false,
            progresscall: false,
            pagination: null,
            loadingClosed: false,
            closedLoaded: false,
            analyticsVisible: false,
            panelMessages: ["⏳ Aguardando novas atualizações..."],
            closedCount: 0,

        }),

        getters: {
            countSelectedAttendances: (state) => {
                return state.selectedAttendances.length
            },

            currentChannel: (state) => {
                const attendance = computed(() => storeAttendance().currentAttendance)
                if (!attendance.value) return {}

                return state.channels.find((channel) => channel.id === attendance.value.channel_id)
            },

            currentChannelConnections: (state) => {
                const attendance = computed(() => storeAttendance().currentAttendance)
                if (!attendance.value) return []

                const channel = state.channels.find((channel) => channel.id === attendance.value.channel_id)
                return Array.isArray(channel?.connections) ? channel.connections : []
            },

            currentConnection: (state) => {
                const attendance = computed(() => storeAttendance().currentAttendance)
                if (!attendance.value) return null

                const connections = Array.isArray(
                    state.channels.find((channel) => channel.id === attendance.value.channel_id)?.connections
                ) ? state.channels.find((channel) => channel.id === attendance.value.channel_id).connections : []

                const selectedId = state.selectedConnections[attendance.value.id]
                if (selectedId == null) return null

                return connections.find(c => c.id === selectedId) || null
            },

            currentChannelDefaultConnection: (state) => {
                const attendance = computed(() => storeAttendance().currentAttendance)
                if (!attendance.value) return null

                const channel = state.channels.find((channel) => channel.id === attendance.value.channel_id)
                const connections = Array.isArray(channel?.connections) ? channel.connections : []

                return connections.find(c => c?.settings?.is_default === true) || null
            },

            findConnectionName: (state) => (connectionId) => {
                if (connectionId == null) return null

                for (const channel of state.channels) {
                    const connection = (Array.isArray(channel?.connections) ? channel.connections : [])
                        .find(c => c.id === connectionId)
                    if (connection) return connection.name
                }

                return null
            },

            internalMessagesActive: (state) => {
                const attendance = computed(() => storeAttendance().currentAttendance)
                if (!attendance.value) return false

                if (attendance.value.chat_step === 'waiting') return true

                return state.internalMessages
            },

            showInternalMessagesButton: () => {
                const attendance = computed(() => storeAttendance().currentAttendance)
                if (!attendance.value) return false

                return attendance.value.chat_step !== 'waiting'
            },

            omnivoipChannels: (state) => {
                return state.channels.filter(ch =>
                    ch.telephony_omnichannel === true
                    || ch.telephony_omnichannel === 'true'
                )
            },

            hasOmnivoipChannels() {
                return this.omnivoipChannels.length > 0
            },
        },

        actions: {
            updateWebsocketStatus(status) {
               // if(status == 'reconnect'){ location.reload() }
                this.websocket.status = status
            },

            togglePreferencesSlideOver() {
                this.layout.showPreferencesSlideOver = !this.layout.showPreferencesSlideOver
            },

            setSelectedConnection(attendanceId, connectionId) {
                if (connectionId == null) {
                    delete this.selectedConnections[attendanceId]
                    return
                }
                this.selectedConnections = { ...this.selectedConnections, [attendanceId]: connectionId }
            },

            toggleNotificationsSlideOver() {
                this.layout.showNotificationsSlideOver = !this.layout.showNotificationsSlideOver
            },

            toggleSidebar() {
                if (window.innerWidth >= 1280) return

                this.layout.showSidebar = !this.layout.showSidebar
            },

            toggleSelectAttendances() {
                this.unselectAllAttendances()
                this.layout.showSelectAttendances = !this.layout.showSelectAttendances
            },

            hideSelectAttendances() {
                this.unselectAllAttendances()
                this.layout.showSelectAttendances = false
            },

            toggleInternalMessages() {
                this.internalMessages = !this.internalMessages
            },

            selectAllAttendances(payload) {


                this.selectedAttendances = payload.map(attendance => attendance.id)
            },

            unselectAllAttendances() {
                this.selectedAttendances = []
            },

            /** @param {int} id */
            selectAttendance(id) {

                // Add or remove attendance from selectedAttendances
                if (this.selectedAttendances.includes(id)) {
                    this.selectedAttendances = this.selectedAttendances.filter((attendance) => attendance !== id)
                    return
                }


                this.selectedAttendances.push(id)
            },

            filterTerm(term) {
                this.filteredTerm = term
            },
            filterChannel(id) {
                if (this.filteredChannels.includes(id)) {
                    this.filteredChannels = this.filteredChannels.filter((user) => user !== id)
                    return
                }
                this.filteredChannels.push(id)
            },
            filterDepartment(id) {
                if (this.filteredDepartaments.includes(id)) {
                    this.filteredDepartaments = this.filteredDepartaments.filter((user) => user !== id)
                    return
                }
                this.filteredDepartaments.push(id)
            },

            /** @param {int} id */
            filterUser(id) {
                if (this.filteredUsers.includes(id)) {
                    this.filteredUsers = this.filteredUsers.filter((user) => user !== id)
                    return
                }
                this.filteredUsers.push(id)
            },

            countAttendancesInTitle() {
                const title = document.title
                const count = 123

                if (count === 0) return

                document.title = `(${count}) ${title}`
            },

            /** @param {string} order */
            orderAttendances(order) {
                this.attendancesRoute.sort((a, b) => {
                    const ta = Number(a.last_message?.timestamp || 0)
                    const tb = Number(b.last_message?.timestamp || 0)
                    if (order === 'asc') return ta - tb
                    return tb - ta
                })
            },

            /** @param {int} id */
            setCurrentAttendance(id) {
                this.attendanceActive = this.attendancesRoute.find((attendance) => attendance.id === id)
            },

            getChannel(id) {
                return this.channels.find((channel) => channel.id === id)
            },

            currentChannelIcon: (type) => {
                const storeChannel = useChannelsStore()
                return storeChannel.defineIcon(type)
            },
            removeTag(attendance_id, tag_id) {

                const currentTags = storeAttendance().currentAttendance?.tags
                if (Array.isArray(currentTags)) {
                    storeAttendance().currentAttendance.tags = currentTags.filter((tag) => (typeof tag === 'object' && tag !== null ? tag.id : tag) !== tag_id)
                }
                axios.delete(`/v1/chat/attendances/${attendance_id}/tags/${tag_id}`)
                .then(res => {


                        const response = res.data

                        if (response) {



                           notify({
                            type: 'success',
                            title: 'Pronto',
                            text: 'Tag removida com sucesso',
                        })


                        }

                })

            },
            addTag(attendance_id, tag_id) {

                axios.post(`${getUrlApiV1()}/chat/attendances/${attendance_id}/tags?tag_id=${tag_id}`)
                .then(res => {

                    //console.log('RESPOSTA TAG',res)
                        const response = res.data

                        if (!response.error) {

                            if(!storeAttendance().currentAttendance.tags){
                                storeAttendance().currentAttendance.tags = []
                            }
                            const tag = this.tags.find((item) => item.id === tag_id)
                            storeAttendance().currentAttendance?.tags?.push(tag ?? tag_id)

                            notify({
                                type: 'success',
                                title: 'Pronto',
                                text: 'Tag atribuída com sucesso ao atendimento',
                            })
                        }

                }).catch(err => {
                    console.warn('ERRO AO ATRIBUIR A TAG =>>>>>>>>>>>>>>>>>',err)
                    notify({
                        type: 'error',
                        title: 'Erro',
                        text: 'Não foi possível atribuir a tag ao atendimento',
                    })
                })

            },
            async getTemplates(){

                axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'
                axios.get('/messages/templates?status=approved')
                    .then(res => {
                            const response = res.data
                            this.templates = response
                    }).finally(()=> {
                        axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'
                    })

            },
            async getTags() {

                 const BASEURL =  axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINT|| window.location.origin + '/api_v2/api'

                await axios.get(`${BASEURL}/v1/tags?type=attendance`)
                    .then(res => {

                        const response = res.data

                        this.tags = response.data
                    })

                    axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINT  || window.origin + '/api-v2'

                    await axios.get(`${BASEURL}/v1/tags?type=client`)
                    .then(res => {

                        const response = res.data

                        this.tagsClient = response.data
                    }).finally(()=> {
                        axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'
                    })

                    axios.defaults.baseURL =  getUrlBaseV2()

                    await axios.get('/tag/user')
                    .then(res => {

                        const response = res.data
                        this.tagsUser = response.data

                    })
                    // await axios.get('/tag/kanban')
                    // .then(res => {

                    //     const response = res.data
                    //     this.tagsKanban = response.data

                    // }).finally(()=> {
                    //     axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'
                    // })


            },

            getFastMessages() {

                axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'
                axios.get('/chat/messages/models')
                .then(res => {

                        const response = res.data

                        this.messagesFast = response

                }).finally(()=> {
                    axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'
                })

            },

            getFastMessagesToChannel(channelId) {

                // if(typeof channelId === 'number') channelId = channelId.toString()
                // console.log(this.messagesFast, channelId)

                return this.messagesFast.filter(message => {
                    return message.channels === null || message.channels.includes(channelId);
                });
            },
            getClosedCount(){

                const base = getUrlBaseV2()
                axios.get(`${base}/attendances/closed/total`)
                .then(res => {
                    const response = res.data
                    this.closedCount = response.total
                })
            },

            getClosedDaily(sort, only_count = false, { silent = false } = {}) {

                if(!sort) sort = 'desc'
                if (!silent) this.loadingClosed = true;
                axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'
                axios.get(`/attendances/closed?order=${sort}${only_count? '&only_count=true' : ''}`)
                    .then(res => {
                            const response = res.data
                            if(!only_count){
                                this.attendances.closed = response
                                this.closedLoaded = true



                            } else {
                                storeAttendance().closedStats = response

                                const totalClosed = Object.values(response.total_by_user)
                                .reduce((sum, userObj) => sum + (userObj.count || 0), 0);
                            this.closedCount = totalClosed;

                            }
                            if (!silent) this.loadingClosed = false;
                    }).finally(()=> {
                        axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'
                    })

            },
            refreshClosedDaily() {
                if (!this.analyticsVisible) return
                if (closedRefetchTimer) clearTimeout(closedRefetchTimer)
                closedRefetchTimer = setTimeout(() => {
                    closedRefetchTimer = null
                    if (!this.analyticsVisible) return
                    this.getClosedDaily('desc', false, { silent: true })
                    this.getClosedCount()
                }, 2000)
            },
            getSurveyOptions(){

                axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'

                axios.get('/satisfaction-survey?status=active&no_content=true')
                .then(res => {

                    this.surveyOptions = res.data

                }).finally(()=> {

                axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'

            })
            },
            getAutomationOptions(){

                axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'

                axios.get('/campaigns/automations')
                .then(res => {

                    const data = res.data?.data ?? res.data ?? []
                    const list = Array.isArray(data) ? data : []
                    this.automationOptions = list.filter((item) => item.status === 'active')

                }).finally(()=> {

                axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'

            })
            },
            getChatConfig(){

                axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'
                axios.get('/chat/configs')
                    .then(res => {

                        const response = res.data
                        this.chatConfig = response

                        this.getSurveyOptions();
                        this.getAutomationOptions();

                        if(response.select_satisfaction_survey){


                        }
                        if(response.chat_sidebar_collapse){
                            this.layout.showSidebar = true
                        }

                    }).finally(()=> {
                            //satisfaction-survey?status=active&no_content=true


                        axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'

                    })

            },

            async fetchAttendancesInfinite(path, onPage) {
                const pageSize = 50
                const maxIterations = 200
                let beforeId = null
                let previousLastId = null

                for (let iteration = 0; iteration < maxIterations; iteration++) {
                    axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'
                    const response = await axios.get(path, {
                        params: {
                            with_last_message: true,
                            infinite_scroll: true,
                            before_at: beforeId,
                        },
                    })

                    const data = response.data.attendances

                    if (!data || data.length === 0) {
                        break
                    }

                    onPage(data)

                    // Back pagina de 50 em 50 no formato infinite_scroll; menos que isso é a última página
                    if (data.length < pageSize) {
                        break
                    }

                    // Se o cursor não avançou (mesma página retornada), interrompe para não entrar em loop
                    const lastId = data[data.length - 1].id
                    if (lastId === previousLastId) {
                        break
                    }
                    previousLastId = lastId

                    beforeId = data[data.length - 1].last_activity
                    await new Promise((resolve) => setTimeout(resolve, 1000))
                }
            },

            pushUniqueAttendance(list, item) {
                if (!list.find((existing) => existing.id === item.id)) {
                    list.push(item)
                    return true
                }
                return false
            },

            async loadAllAttendances() {

                this.loading.attendancesList = true

                try {
                    await this.fetchAttendancesInfinite('/attendances/audit/in-progress', (data) => {
                        data.forEach((item) => {
                            this.pushUniqueAttendance(storeAttendance().audit.progress, item)

                            if (item.user_id == storeUser().me?.id) {
                                const added = this.pushUniqueAttendance(storeAttendance().progress, item)
                                if (added) {
                                    storeMessages().newMessagesCount[item.id] = item.unread_messages
                                }
                            }
                        })
                    })
                } catch (error) {
                    console.error('Erro ao carregar as atendimentos:', error)
                } finally {
                    this.loading.attendancesList = false
                    this.loading.waitingList = true

                    try {
                        await this.fetchAttendancesInfinite('/attendances/audit/waiting', (data) => {
                            data.forEach((item) => {
                                this.pushUniqueAttendance(storeAttendance().audit.waiting, item)
                            })
                        })
                    } catch (error) {
                        console.error('Erro ao carregar os atendimentos em espera (audit):', error)
                    } finally {
                        this.loading.waitingList = false
                        this.loading.requestAttendances = false
                        axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'
                    }
                }




              },
            async getAttendances(date){



                this.loading.waitingList = true
                axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'

                const waitingPromise = this.fetchAttendancesInfinite('/attendances/waiting', (data) => {
                    data.forEach((item) => {
                        this.pushUniqueAttendance(storeAttendance().waiting, item)
                    })
                }).catch((error) => {
                    console.error('Erro ao carregar os atendimentos em espera:', error)
                }).finally(() => {
                    this.loading.waitingList = false
                    this.loading.requestAttendances = false
                })

                axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'

                const Automation = axios.get(`/attendances/automation?with_last_message=true&infinite_scroll=true`)
                .then(res => {  const response = res.data.attendances; storeAttendance().automation = response }).finally(()=> { this.loading.requestAttendances = false })

                await waitingPromise
                await Automation


                axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'


                /*this.loading.requestAttendances = true

                axios.defaults.baseURL = getUrlBaseV2()

                const ProgressAudit = await axios.get(`/attendances/audit/in-progress?with_last_message=true&infinite_scroll=true`)
                .then(res => {

                    const response = res.data.attendances;
                    storeAttendance().counters.audit.progress = res.data.total;
                    storeAttendance().audit.progress = response

                    response.forEach(item => {
                        if(item.unread_messages > 0){
                            //console.log(item.id)
                            storeMessages().newMessagesCount[item.id] = item.unread_messages
                        }
                        if(item.is_favorite){
                            storeAttendance().progress.push(item)
                        }
                });

                }).finally(()=> { this.loading.requestAttendances = false })

                axios.defaults.baseURL = getUrlBaseV2()

                const WaitingAudit = await axios.get(`/attendances/audit/waiting?with_last_message=true&infinite_scroll=true`)
                .then(res => {  const response = res.data.attendances; storeAttendance().waiting.progress = response }).finally(()=> { this.loading.requestAttendances = false })

                axios.defaults.baseURL = getUrlBaseV2()

                const Progress = await axios.get(`/attendances/in-progress?with_last_message=true&infinite_scroll=true`)
                .then(res => {  const response = res.data.attendances; storeAttendance().progress = response }).finally(()=> { this.loading.requestAttendances = false })

                axios.defaults.baseURL = getUrlBaseV2()

                const Waiting = await axios.get(`/attendances/waiting?with_last_message=true&infinite_scroll=true`)
                .then(res => {  const response = res.data.attendances; storeAttendance().waiting = response }).finally(()=> { this.loading.requestAttendances = false })

                axios.defaults.baseURL = getUrlBaseV2()

                const Automation = await axios.get(`/attendances/automation?with_last_message=true&infinite_scroll=true`)
                .then(res => {  const response = res.data.attendances; storeAttendance().automation = response }).finally(()=> { this.loading.requestAttendances = false })

                axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'

    */

                /*axios.defaults.baseURL =  import.meta.env.VITE_API_ENDPOINTLOCAL || window.origin + '/api-v2'
                await axios.get(`/attendances?with_last_message=true&infinite_scroll=true`)
                        .then(res => {

                            const response = res.data;

                            storeAttendance().progress = response.progress
                            storeAttendance().waiting = response.waiting
                            storeAttendance().automation = response.automation
                            storeAttendance().pagination = response.pagination


                            if (response?.audit) {
                                storeAttendance().audit.progress = response.audit.progress
                                storeAttendance().audit.waiting = response.audit.waiting

                                response.progress.forEach(item => {
                                    if(item.unread_messages > 0){

                                        storeMessages().newMessagesCount[item.id] = item.unread_messages
                                    }

                                });

                                response.audit.progress.forEach(item => {
                                        if(item.unread_messages > 0){
                                            //console.log(item.id)
                                            storeMessages().newMessagesCount[item.id] = item.unread_messages
                                        }
                                        if(item.is_favorite){
                                            storeAttendance().progress.push(item)
                                        }
                                });
                            }

                        })
                        .finally(()=> {
                            this.loading.requestAttendances = false
                            axios.defaults.baseURL = import.meta.env.VITE_API_ENDPOINT || window.origin + '/api_v2/api'

                        })*/

            },
            async initializeChat() {
                const router = useRouter()
                this.loading.requestAttendances = true
                const storeUser = useUsersStore

                const { isAdmin } = storeUser()

                const BASEURL =  import.meta.env.VITE_API_ENDPOINT|| window.location.origin + '/api_v2/api'

                await axios.get(BASEURL + `/v1/chat/initialize?ignore_attendances=true&include_me=true&origin=${ isAdmin ? 'audit' : 'chat'}`)
                    .then(res => {
                        const response = res.data

                        /*storeAttendance().progress = response.attendances.progress
                        storeAttendance().waiting = response.attendances.waiting
                        storeAttendance().automation = response.attendances.automation
                        if (response?.attendances_audit) {
                            storeAttendance().audit.progress = response.attendances_audit.progress
                            storeAttendance().audit.waiting = response.attendances_audit.waiting
                        }*/

                        if (response?.to_users) {
                            this.toUsers = response.to_users
                        }

                        this.channels = response.channels
                        this.users = response.users
                        this.departaments = response.departaments
                        this.websocket.token = response.websocket.token


                        startWebSocket(response.websocket.path, response.websocket.token, true)

                        setTimeout(() => {
                            const currentRoute = router.currentRoute.value
                            if (!storeAttendance().currentAttendance && currentRoute.params.id) {
                                router.push({ name: currentRoute.name })
                            }

                            if (storeAttendance().currentAttendance) {
                                storeMessages().requestMessages(storeAttendance().currentAttendance.id)
                            }
                        }, 200)
                    })
                    .finally(() => {
                        this.loading.requestAttendances = false
                        //ping para receber os usuários online
                        setTimeout(()=> {

                            sendWebSocketMessage({"type": "ping", "user": storeUser().me?.id, "tokenValidate": this.websocket.token })

                        }, 5000)

                    })
                    this.getChatConfig()
                    this.getTags()
                    this.getAttendances()
                    this.loadAllAttendances()
                    this.getTemplates()

            },

            /**
             * @param {Number|null} meId
             * @returns {Number[]}
             */
            toAllWithoutMe(meId = null) {
                const me = meId || storeUser().me?.id

                const excluded = [me, Number(`${me}5555`), Number(`${me}8888`)]

                return this.toUsers?.all?.filter(value => !excluded.includes(value))
            },
        },
    })
}
